import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Eye } from "lucide-react";
import { Product, SubVariant } from "@/data/products";
import { useLanguage } from "@/i18n/LanguageContext";
import { useFavorites } from "@/hooks/useFavorites";
import { useCart } from "@/contexts/CartContext";
import { toast } from "@/hooks/use-toast";
import FavoriteButton from "@/components/FavoriteButton";
import { isOfferActive, calcDiscountedPrice, getOfferBadge } from "@/utils/offerUtils";
import StockIndicator from "@/components/StockIndicator";
import CountdownTimer from "@/components/CountdownTimer";
import LazyImage from "@/components/LazyImage";
import { optimizeImageUrl, buildSrcSet } from "@/utils/imageTransform";
import { trackPrefetchFire } from "@/utils/prefetchTelemetry";
import { flyToCart, announceCartUpdate, burstFromElement } from "@/utils/flyToCart";
import { productPath } from "@/utils/shareLinks";
import SaleSticker from "@/components/SaleSticker";
import ProductBadge from "@/components/ProductBadge";
import QuickViewModal from "@/components/QuickViewModal";
import VariantSwatches from "@/components/VariantSwatches";
import { getSubVariants } from "@/utils/productVariants";
import { variantDisplayLabel } from "@/utils/variantLabel";


interface ProductCardProps {
 product: Product;
 /**
 * Mark this card as above-the-fold. Its image loads eagerly with
 * fetchpriority=high so the first product row can contribute to LCP
 * without waiting for the IntersectionObserver.
 */
 priority?: boolean;
 /**
 * When set, hovering or focusing this card warms the browser cache for
 * the given image URL (typically the next card in the grid). Makes rapid
 * left-to-right browsing feel instant.
 */
 prefetchNext?: string;
}

const subVariantKey = (v: SubVariant) =>
 (v.colorHex ?? v.scent ?? v.poeticName ?? "").toString();

const ProductCard = ({ product, priority = false, prefetchNext }: ProductCardProps) => {
 const { isAr } = useLanguage();
 const [quickViewOpen, setQuickViewOpen] = useState(false);
 const [previewVariant, setPreviewVariant] = useState<SubVariant | null>(null);
 const [selectedVariant, setSelectedVariant] = useState<SubVariant | null>(null);
 // Dynamic card frame: the image dictates the card's aspect ratio instead
 // of a rigid 1:1 window that letterboxed non-square photos with big ivory
 // gaps. We clamp the natural ratio to a tasteful band so the grid stays
 // rhythmic (nothing extreme like ultra-panoramic strips) while every
 // photo fills its frame edge-to-edge via object-cover. Portrait photos
 // make the card taller, landscape photos make it shorter, the grid
 // uses `items-start` so heights stay honest instead of stretching.
 const MIN_RATIO = 0.75; // portrait floor (3:4)
 const MAX_RATIO = 1.35; // landscape ceiling (~4:3)
 const [aspectRatio, setAspectRatio] = useState<number>(1);
 const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
 const el = e.currentTarget;
 if (!el.naturalWidth || !el.naturalHeight) return;
 const ratio = el.naturalWidth / el.naturalHeight;
 const clamped = Math.min(MAX_RATIO, Math.max(MIN_RATIO, ratio));
 setAspectRatio(clamped);
 };

 const { isFavorite, toggleFavorite } = useFavorites();
 const { addItem } = useCart();
 const navigate = useNavigate();
 const name = isAr ? product.nameAr : product.name;
 const desc = isAr ? product.descriptionAr : product.description;
 const badge = isAr ? product.badgeAr : product.badge;
 const subVariants = useMemo(() => getSubVariants(product), [product]);
 // Hydrate the card's swatch selection from the Quick View storage so the last
 // pick (from either surface) is reflected as soon as the grid renders.
 useEffect(() => {
 if (typeof window === "undefined") return;
 if (subVariants.length === 0) return;
 try {
 const raw = window.localStorage.getItem(`nb:quickview:${product.id}`);
 if (!raw) return;
 const parsed = JSON.parse(raw) as { subId?: string | null };
 if (!parsed?.subId) return;
 const match = subVariants.find((v) => v.id === parsed.subId);
 if (match) setSelectedVariant((prev) => prev ?? match);
 } catch {
 // ignore malformed storage entries
 }
 }, [product.id, subVariants]);
 const activeVariant = previewVariant ?? selectedVariant;
 const overlayImage = activeVariant?.imageUrl || null;

 // Calculate price display with offers
 const getDisplayPrice = () => {
 if (product.sizeVariants) {
 const first = product.sizeVariants[0];
 const last = product.sizeVariants[product.sizeVariants.length - 1];
 
 // Check if any variant has an active offer
 const hasAnyOffer = product.sizeVariants.some(v => isOfferActive(v.offer)) || isOfferActive(product.offer);
 
 if (hasAnyOffer) {
 const firstOffer = first.offer && isOfferActive(first.offer) ? first.offer : product.offer;
 const lastOffer = last.offer && isOfferActive(last.offer) ? last.offer : product.offer;
 const firstDiscounted = calcDiscountedPrice(first.price, firstOffer);
 const lastDiscounted = calcDiscountedPrice(last.price, lastOffer);
 
 return {
 hasDiscount: true,
 original: `${first.price.toFixed(0)}, ${last.price.toFixed(0)} EGP`,
 discounted: `${firstDiscounted.toFixed(0)}, ${lastDiscounted.toFixed(0)} EGP`,
 };
 }
 
 return {
 hasDiscount: false,
 original: `${first.price.toFixed(0)}, ${last.price.toFixed(0)} EGP`,
 discounted: "",
 };
 }
 
 const offerActive = isOfferActive(product.offer);
 if (offerActive) {
 return {
 hasDiscount: true,
 original: `${product.price.toFixed(0)} EGP`,
 discounted: `${calcDiscountedPrice(product.price, product.offer).toFixed(0)} EGP`,
 };
 }
 
 return {
 hasDiscount: false,
 original: `${product.price.toFixed(0)} EGP`,
 discounted: "",
 };
 };

 const priceInfo = getDisplayPrice();

 // Get the active offer (product level or first matching variant)
 const activeOffer = isOfferActive(product.offer)
 ? product.offer
 : product.sizeVariants?.find(v => isOfferActive(v.offer))?.offer;

 // Dedupe per instance so repeated hovers don't refire the request.
 const prefetchedRef = useMemo(() => ({ done: false }), []);
 const warmNext = () => {
 if (!prefetchNext || prefetchedRef.done) return;
 prefetchedRef.done = true;
 // Match the sharp variant the neighboring card will actually request.
 const url = optimizeImageUrl(prefetchNext, { width: 700, quality: 78 });
 trackPrefetchFire(url);
 const img = new Image();
 img.decoding = "async";
 img.loading = "eager";
 img.src = url;
 };

 return (
 <>
 <Link
 to={productPath(product.id)}
 onMouseEnter={warmNext}
 onFocus={warmNext}
 onTouchStart={warmNext}
 className="group flex flex-col h-full bg-card border border-border rounded-sm overflow-hidden transition-all duration-500 hover:border-primary hover:shadow-[0_8px_30px_-10px_hsl(43_52%_54%_/_0.3)]"
 >
 <div
 className="relative overflow-hidden bg-background"
 style={{ aspectRatio }}
 >
 {/* Every photo is object-cover on a dynamically-sized frame: the
 card grows/shrinks with the image's natural ratio (clamped to
 3:4, 4:3) so there's no ivory letterbox and nothing looks
 stranded in blank space. */}
 <LazyImage
 src={optimizeImageUrl(product.image, { width: 700, quality: 78 })}
 srcSet={buildSrcSet(product.image, [300, 500, 700, 900], 78)}
 avifSrcSet={buildSrcSet(product.image, [300, 500, 700, 900], 78, "avif")}
 webpSrcSet={buildSrcSet(product.image, [300, 500, 700, 900], 78, "webp")}
 sizes="(min-width: 1280px) 22vw, (min-width: 768px) 33vw, 50vw"
 width={700}
 height={700}
 placeholder={optimizeImageUrl(product.image, { width: 24, quality: 30 })}
 eager={priority}
 fetchPriority={priority ? "high" : undefined}
 alt={`${name} by Nil Beauty, ${product.category?.replace(/-/g, " ") || "natural skincare"}, ${product.shortDescription || product.description.slice(0, 90)}, made in Egypt`}
 title={`Nil Beauty ${name}`}
 onLoad={handleImageLoad}
 className="relative w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
 />
 {/* Sub-variant image overlay, cross-fades when hovering a swatch.
 Uses object-cover to match the base image so the swap doesn't
 shift crop or reveal any background. */}
 {overlayImage && (() => {
 const overlaySizes = "(min-width: 1280px) 22vw, (min-width: 768px) 33vw, 50vw";
 const avif = buildSrcSet(overlayImage, [300, 500, 700, 900], 78, "avif");
 const webp = buildSrcSet(overlayImage, [300, 500, 700, 900], 78, "webp");
 return (
 <picture>
 {avif && <source type="image/avif" srcSet={avif} sizes={overlaySizes} />}
 {webp && <source type="image/webp" srcSet={webp} sizes={overlaySizes} />}
 <img
 src={optimizeImageUrl(overlayImage, { width: 700, quality: 78 })}
 srcSet={buildSrcSet(overlayImage, [300, 500, 700, 900], 78)}
 sizes={overlaySizes}
 alt=""
 aria-hidden="true"
 loading="lazy"
 decoding="async"
 className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300 opacity-100 pointer-events-none"
 />
 </picture>
 );
 })()}
 {badge && (
 <ProductBadge className={`absolute top-3 ${isAr ? "right-3" : "left-3"} text-[11px] px-3 py-1 max-w-[calc(100%-5rem)]`}>
 {badge}
 </ProductBadge>
 )}
 {activeOffer && (
 <div className={`absolute ${badge ? "top-12" : "top-3"} ${isAr ? "right-2" : "left-2"} z-10 pointer-events-none`}>
 <SaleSticker offer={activeOffer} isAr={isAr} size={68} />
 </div>
 )}
 <div className={`absolute top-3 ${isAr ? "left-3" : "right-3"}`}>
 <FavoriteButton
 isFavorite={isFavorite(product.id)}
 onToggle={() => toggleFavorite(product.id, { productName: product.name })}
 size={16}
 />
 </div>
 {/* Quick View trigger, appears on hover / always visible on touch */}
 <button
 type="button"
 onClick={(e) => {
 e.preventDefault();
 e.stopPropagation();
 setQuickViewOpen(true);
 }}
 aria-label={isAr ? "عرض سريع" : "Quick view"}
 className={`absolute bottom-3 ${isAr ? "left-3" : "right-3"} inline-flex items-center gap-1.5 rounded-full bg-background/90 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-foreground/85 shadow-sm border border-border/60 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity`}
 >
 <Eye size={12} aria-hidden="true" />
 {isAr ? "عرض سريع" : "Quick view"}
 </button>
 {/* Low stock / sold out indicator */}
 <div className={`absolute bottom-2 ${isAr ? "right-2" : "left-2"}`}>
 <StockIndicator stockQuantity={product.stockQuantity} stockStatus={product.stockStatus} />
 </div>
 </div>
        <div className="p-5 flex flex-col flex-1 min-h-0">
          <h3 className="font-serif text-lg font-semibold mb-1 group-hover:text-primary transition-colors duration-300">
            {name}
          </h3>
          {/* Brief expands (up to 8 lines) to soak up extra row height, so
              cards align at the bottom without leaving a big ivory gap. */}
          <p className="font-body text-sm text-muted-foreground mb-3 overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:8]">{desc}</p>
 <div className="flex items-center gap-2 flex-wrap">
 {priceInfo.hasDiscount ? (
 <>
 <p className="font-serif text-lg font-bold text-destructive">{priceInfo.discounted}</p>
 <p className="font-serif text-sm text-muted-foreground line-through">{priceInfo.original}</p>
 </>
 ) : (
 <p className="font-serif text-lg font-bold text-primary">{priceInfo.original}</p>
 )}
 </div>
 {product.sizeVariants && product.sizeVariants.length > 0 && (
 <div className="flex flex-wrap gap-1.5 mt-2">
 {product.sizeVariants.map((v) => {
 const lbl = variantDisplayLabel(v, isAr, product) || v.size;
 return (
 <ProductBadge
 key={v.id || v.size}
 variant="muted"
 className="text-[11px] px-2.5 py-1"
 dir="ltr"
 >
 {lbl}
 </ProductBadge>
 );
 })}
 </div>
 )}
 {(!product.sizeVariants || product.sizeVariants.length === 0) && product.size && (
 <div className="flex flex-wrap gap-1.5 mt-2">
 <ProductBadge variant="muted" className="text-[11px] px-2.5 py-1" dir="ltr">{product.size}</ProductBadge>
 </div>
 )}
 {/* Sub-variant swatches (scent / color), hover previews the image */}
 {subVariants.length > 0 && (
 <VariantSwatches
 variants={subVariants}
 isAr={isAr}
 className="mt-3"
 selectedKey={selectedVariant ? subVariantKey(selectedVariant) : null}
 onPreview={setPreviewVariant}
 onSelect={(v) => setSelectedVariant(v)}
 />
 )}
 {selectedVariant && (
 <p
 className="mt-2 font-body text-[11px] uppercase tracking-[0.16em] text-primary"
 aria-live="polite"
 >
 {isAr
 ? `المختار: ${selectedVariant.poeticNameAr || selectedVariant.scentAr || selectedVariant.poeticName || selectedVariant.scent || ""}`
 : `Selected: ${selectedVariant.poeticName || selectedVariant.scent || ""}`}
 </p>
 )}
 {/* Offer countdown on card */}
 {product.offer?.endDate && isOfferActive(product.offer) && (
 <CountdownTimer endDate={product.offer.endDate} compact className="mt-2" />
 )}
 {/* Add to Cart */}
 <button
 type="button"
 onClick={(e) => {
 e.preventDefault();
 e.stopPropagation();
 const btn = e.currentTarget as HTMLButtonElement;
 const soldOut = product.stockStatus === "out_of_stock" || product.stockQuantity === 0;
 if (soldOut || btn.dataset.busy === "1") return;
 burstFromElement(btn);
 if (product.sizeVariants && product.sizeVariants.length > 0) {
 navigate(productPath(product.id));
 return;
 }
 btn.dataset.busy = "1";
 try {
 addItem(product.id, null);
 const card = btn.closest("a");
 const img = card?.querySelector("img") as HTMLElement | null;
 const message = isAr ? `تمت إضافة ${name} إلى السلة` : `${name} added to cart`;
 const scheduled = flyToCart(img, product.image, { announce: message });
 if (!scheduled) announceCartUpdate(message);
 toast({
 title: isAr ? "تمت الإضافة إلى السلة" : "Added to cart",
 description: name,
 });
 } catch (err) {
 toast({
 title: isAr ? "تعذر إضافة المنتج" : "Could not add to cart",
 description: isAr ? "حاول مرة أخرى" : "Please try again",
 variant: "destructive",
 });
 } finally {
 window.setTimeout(() => { btn.dataset.busy = ""; }, 400);
 }
 }}
 disabled={product.stockStatus === "out_of_stock" || product.stockQuantity === 0}
 className="mt-auto pt-4 w-full inline-flex items-center justify-center gap-2 bg-foreground text-background hover:bg-primary hover:text-primary-foreground transition-colors duration-300 font-body text-xs sm:text-sm uppercase tracking-widest py-2.5 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
 aria-label={isAr ? "أضف إلى السلة" : "Add to cart"}
 >
 <ShoppingBag size={15} strokeWidth={2} aria-hidden="true" focusable="false" />
 {isAr ? "أضف إلى السلة" : "Add to Cart"}
 </button>
 </div>
 </Link>
 <QuickViewModal
 product={product}
 open={quickViewOpen}
 onOpenChange={setQuickViewOpen}
 initialSubId={selectedVariant?.id ?? null}
 onSubVariantChange={(id) => {
 if (!id) {
 setSelectedVariant(null);
 return;
 }
 const match = subVariants.find((v) => v.id === id);
 if (match) setSelectedVariant(match);
 }}
 />
 </>
 );
};

export default ProductCard;
